import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSVerifyIdentityData,
  type EnrollmentHYCSVerifyIdentitySubmitInput,
  type EnrollmentHYCSVerifyIdentitySubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS VerifyIdentity data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSVerifyIdentityData>
 * @example const response = await getEnrollmentHYCSVerifyIdentityData()
 */
export const getEnrollmentHYCSVerifyIdentityData = async (
  context?: DefaultContext,
): Promise<EnrollmentHYCSVerifyIdentityData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSVerifyIdentityData',
      query: `mutation getEnrollmentHYCSVerifyIdentityData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSVerifyIdentityData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSVerifyIdentityData = createQueryHook<undefined, EnrollmentHYCSVerifyIdentityData>(
  getEnrollmentHYCSVerifyIdentityData,
)

/**
 * Submit Enrollment HYCS VerifyIdentity data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSVerifyIdentitySubmitOutput>
 * @example const response = await submitEnrollmentHYCSVerifyIdentityData();
 */
export const submitEnrollmentHYCSVerifyIdentityData = async (
  enrollmentVerifyIdentitySubmitInput: EnrollmentHYCSVerifyIdentitySubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSVerifyIdentitySubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSVerifyIdentityData',
      query: `mutation submitEnrollmentHYCSVerifyIdentityData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentVerifyIdentitySubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSVerifyIdentitySubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSVerifyIdentityData = createMutationHook<
  EnrollmentHYCSVerifyIdentitySubmitInput,
  EnrollmentHYCSVerifyIdentitySubmitOutput
>(submitEnrollmentHYCSVerifyIdentityData)
